import React, {
  Fragment,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { Menu, Transition } from "@headlessui/react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import cx from "classnames";

export interface SplitButtonAction {
  label: string;
  icon?: IconDefinition;
  onClick: () => void;
  variant?: "default" | "danger";
  disabled?: boolean;
}

interface SplitButtonProps {
  primaryAction: SplitButtonAction;
  secondaryActions: SplitButtonAction[];
  /** Keeps the default narrow menu on mobile; from `sm` up, sizes to fit labels without wrapping. */
  wideMenuOnDesktop?: boolean;
}

/** Syncs fixed menu position when Headless UI Menu opens (hooks must not live in Menu render prop). */
const MenuPositionSync: React.FC<{
  open: boolean;
  updatePosition: () => void;
}> = ({ open, updatePosition }) => {
  useLayoutEffect(() => {
    if (!open) return;

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  return null;
};

const SplitButton: React.FC<SplitButtonProps> = ({
  primaryAction,
  secondaryActions,
  wideMenuOnDesktop = false,
}) => {
  const buttonGroupRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{
    /** Positioned next to the button instead of portalled to `document.body`. */
    inPlace: boolean;
    openUpwards: boolean;
    portalStyle?: { top?: number; bottom?: number; right: number };
  } | null>(null);

  const updatePosition = useCallback(() => {
    const buttonGroup = buttonGroupRef.current;
    if (!buttonGroup) return;

    const rect = buttonGroup.getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight;

    // Rough height estimate, so we can flip the menu without a measure-then-move flicker.
    // Being a little out only affects which side we open on, never whether the menu renders.
    const estimatedHeight = secondaryActions.length * 40 + 8;
    const spaceBelow = viewportHeight - rect.bottom;
    const openUpwards =
      spaceBelow < estimatedHeight + 4 && rect.top > spaceBelow;

    // A menu portalled to the body is unusable inside a modal dialog such as `Sidebar`:
    // Radix sets `pointer-events: none` on the body (so the items can't be clicked),
    // treats a pointer-down on the portal as an outside interaction (dismissing the
    // dialog), and marks the body's other children `aria-hidden`. Staying in the dialog's
    // own subtree avoids all three, at the cost of being clipped by an `overflow` ancestor
    // - which is the trade the portal exists to avoid outside a dialog.
    if (buttonGroup.closest('[role="dialog"]')) {
      setPlacement({ inPlace: true, openUpwards });
      return;
    }

    const right = document.documentElement.clientWidth - rect.right;

    setPlacement({
      inPlace: false,
      openUpwards,
      portalStyle: openUpwards
        ? { bottom: viewportHeight - rect.top + 4, right }
        : { top: rect.bottom + 4, right },
    });
  }, [secondaryActions.length]);

  const renderMenu = (menu: JSX.Element) =>
    placement?.inPlace ? menu : createPortal(menu, document.body);

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <MenuPositionSync open={open} updatePosition={updatePosition} />
          <div ref={buttonGroupRef} className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className="inline-flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--color-primary)" }}
              // With an icon the label is hidden below `sm`, leaving the button with no
              // accessible name on mobile; without one the visible text already names it.
              aria-label={primaryAction.icon ? primaryAction.label : undefined}
            >
              {primaryAction.icon && (
                <FontAwesomeIcon
                  icon={primaryAction.icon}
                  className="w-3.5 h-3.5 sm:hidden"
                />
              )}
              <span
                className={primaryAction.icon ? "hidden sm:inline" : undefined}
              >
                {primaryAction.label}
              </span>
            </button>
            <Menu.Button
              disabled={primaryAction.disabled && secondaryActions.every((a) => a.disabled)}
              className="inline-flex items-center rounded-r-md border-l border-white/30 px-2 py-1.5 text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--color-primary)" }}
              onClick={updatePosition}
            >
              <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3" />
            </Menu.Button>
          </div>
          {placement &&
            renderMenu(
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  className={cx(
                    "w-40 rounded-sm bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                    wideMenuOnDesktop && "sm:w-auto sm:min-w-max",
                    placement.inPlace
                      ? cx(
                          "absolute right-0 z-50",
                          placement.openUpwards ? "bottom-full mb-1" : "mt-1"
                        )
                      : "fixed z-[9999]"
                  )}
                  style={placement.inPlace ? undefined : placement.portalStyle}
                >
                  <div className="py-1">
                    {secondaryActions.map((action) => (
                      <Menu.Item key={action.label} disabled={action.disabled}>
                        <span
                          className={cx(
                            "flex items-center gap-2 px-4 py-2 text-sm",
                            wideMenuOnDesktop && "sm:whitespace-nowrap",
                            action.disabled
                              ? "cursor-not-allowed text-gray-400"
                              : cx(
                                  "cursor-pointer",
                                  action.variant === "danger"
                                    ? "text-red-600 hover:bg-red-50"
                                    : "text-gray-600 hover:bg-gray-100"
                                )
                          )}
                          onClick={action.disabled ? undefined : action.onClick}
                        >
                          {action.icon && (
                            <FontAwesomeIcon
                              icon={action.icon}
                              className={cx(
                                "w-3.5 h-3.5",
                                action.variant === "danger"
                                  ? "text-red-500"
                                  : "text-gray-500"
                              )}
                            />
                          )}
                          {action.label}
                        </span>
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            )}
        </>
      )}
    </Menu>
  );
};

export default SplitButton;
